import { ReactElement, useRef, useEffect, useState, useMemo } from 'react';
import Plotly from 'plotly.js-dist-min';
import ChartCard from '../ChartCard';
import {
  buildTyreDataMap,
  assignMarkerSymbols,
  CRR_CHART_COLORS,
  generateLegendAnnotations,
  createCrrChartLayout,
  createAveragePointsTrace,
  calculateFixedSpanYAxisRange,
  getCombinedTyreName,
  CRR_CHART_PLOTLY_CONFIG,
} from '../../utils/crrChartUtils';
import { toRecordingZoneIso } from '../../utils/sessionTime';

export interface DataFieldConfig {
  /** Field to read traverse data from (e.g., 'traverse', 'deltas_traverse') */
  traverseSource: string;
  /** Field name for Crr data within traverse (e.g., 'Crr_xG5', 'Crr_xG3') */
  traverseField: string;
  /** Fallback field name if primary not found */
  traverseFieldFallback?: string;
  /** Field to read average data from (e.g., 'average', 'deltas') */
  averageSource: string;
  /** Field name for Crr average (e.g., 'Crr_xG5', 'Crr_xG3') */
  averageField: string;
  /** Fallback field name if primary not found */
  averageFieldFallback?: string;
}

export interface BaseCrrTimeOfDayPlotProps {
  session: any;
  equipmentCatalog: any;
  pressureUnit: 'Bar' | 'PSI';
  height?: number;
  /** Chart title */
  title: string;
  /** Y-axis title */
  yAxisTitle: string;
  /** Label for hover tooltip (e.g., "Crr Average", "Crr Raw Average") */
  hoverLabel: string;
  /** Message when no data available */
  noDataMessage: string;
  /** Configuration for data field selection */
  dataFieldConfig: DataFieldConfig;
  /** Fixed Y-axis span (if set, centers data within this span) */
  fixedYAxisSpan?: number;
  /** Suffix for trace names */
  traceSuffix?: string;
}

export default function BaseCrrTimeOfDayPlot({
  session,
  equipmentCatalog,
  pressureUnit,
  height = 400,
  title,
  yAxisTitle,
  hoverLabel,
  noDataMessage,
  dataFieldConfig,
  fixedYAxisSpan,
  traceSuffix = 'avg',
}: BaseCrrTimeOfDayPlotProps): ReactElement {
  const plotRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);

  const plotData = useMemo(() => {
    if (!session?.configs) {
      return { traces: [], customLegendData: {} };
    }

    // Build tyre data structures
    const tyreData = buildTyreDataMap(session.configs, equipmentCatalog);
    const tyrePressureMarkers = assignMarkerSymbols(tyreData);

    const traces: any[] = [];
    let configColorIndex = 0;
    // Track per-config colors for legend sync
    const configColorEntries: {
      tyreName: string; pressure: number;
      color: string; symbol: string; letter: string;
    }[] = [];

    // Process configs and create traces
    session.configs.forEach((config: any) => {
      if (!config.runs) return;
      if (config.settings_collection?.exclude_tyre_report) return;

      const tyreId = config.tyre_rear_id;
      const tyrePressure = config.tyre_rear_pressure;
      if (!tyreId || tyrePressure === null || tyrePressure === undefined) return;

      const tyreName = getCombinedTyreName(config.tyre_front_id, tyreId, equipmentCatalog);
      // Assign distinct color per config
      const color = CRR_CHART_COLORS[
        configColorIndex % CRR_CHART_COLORS.length
      ];
      configColorIndex++;
      const markerSymbol = tyrePressureMarkers[tyreName]?.[tyrePressure] || 'circle';
      const pressureDisplay = `${tyrePressure} Bar`;
      const legendName = `${tyreName} - ${pressureDisplay} - Cfg ${config.letter}`;

      configColorEntries.push({
        tyreName, pressure: tyrePressure,
        color, symbol: markerSymbol,
        letter: config.letter || '',
      });

      // Collect run averages
      const averagePoints: { x: string; y: number; run: number }[] = [];

      config.runs.forEach((run: any, runIndex: number) => {
        // Get traverse source (e.g., run.traverse or run.deltas_traverse)
        const traverseSourceData = run[dataFieldConfig.traverseSource];
        if (!traverseSourceData) return;

        const runTimestamp = run.average?.['run_ts'];

        // Get Crr data with fallback
        const crrData = traverseSourceData[dataFieldConfig.traverseField] ||
          (dataFieldConfig.traverseFieldFallback ? traverseSourceData[dataFieldConfig.traverseFieldFallback] : null);

        // Get average source (e.g., run.average or run.deltas)
        const averageSourceData = run[dataFieldConfig.averageSource];

        // Get run average with fallback
        let runAverage = averageSourceData?.[dataFieldConfig.averageField];
        if ((runAverage === null || runAverage === undefined) && dataFieldConfig.averageFieldFallback) {
          runAverage = averageSourceData?.[dataFieldConfig.averageFieldFallback];
        }

        if (runTimestamp && crrData && runAverage !== null && runAverage !== undefined) {
          const originalTime = new Date(runTimestamp);
          const timeIntervalSeconds = 30;
          const midTimeMs =
            originalTime.getTime() + Math.floor(crrData.length / 2) * timeIntervalSeconds * 1000;
          const midTime = new Date(midTimeMs);

          const avgValue = Array.isArray(runAverage) ? runAverage[0] : runAverage;
          averagePoints.push({
            x: toRecordingZoneIso(midTime, run.timezone),
            y: avgValue,
            run: runIndex + 1,
          });
        }
      });

      // Create trace for this config
      const trace = createAveragePointsTrace(averagePoints, {
        tyreName,
        tyrePressure,
        color,
        markerSymbol,
        legendName,
        hoverLabel,
        traceSuffix,
      });

      if (trace) {
        traces.push(trace);
      }
    });

    // Build legend data from actual config colors
    const customLegendData: Record<string, {
      pressures: number[]; colors: string[];
      symbols: string[]; letters: string[];
    }> = {};
    configColorEntries.forEach((entry) => {
      if (!customLegendData[entry.tyreName]) {
        customLegendData[entry.tyreName] = {
          pressures: [], colors: [],
          symbols: [], letters: [],
        };
      }
      const d = customLegendData[entry.tyreName];
      d.pressures.push(entry.pressure);
      d.colors.push(entry.color);
      d.symbols.push(entry.symbol);
      d.letters.push(entry.letter);
    });

    return { traces, customLegendData };
  }, [session, equipmentCatalog, dataFieldConfig, hoverLabel, traceSuffix]);

  useEffect(() => {
    const plotElement = plotRef.current;
    if (!plotElement) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    if (plotData.traces.length > 0) {
      // Generate legend annotations
      const annotations = generateLegendAnnotations(plotData.customLegendData, pressureUnit);

      // Calculate Y-axis range if fixed span is specified
      let yAxisRange: [number, number] | undefined;
      if (fixedYAxisSpan) {
        yAxisRange = calculateFixedSpanYAxisRange(plotData.traces, fixedYAxisSpan);
      }

      // Create layout
      const layout = createCrrChartLayout({
        title,
        yAxisTitle,
        yAxisRange,
        height,
        annotations,
      });

      Plotly.newPlot(plotElement, plotData.traces, layout, CRR_CHART_PLOTLY_CONFIG).then(() => {
        setIsLoading(false);
      });
    } else {
      Plotly.purge(plotElement);
      setIsLoading(false);
    }

    return () => {
      if (plotElement) {
        Plotly.purge(plotElement);
      }
    };
  }, [plotData, pressureUnit, height, title, yAxisTitle, fixedYAxisSpan]);

  if (plotData.traces.length === 0) {
    return (
      <div
        className="cr-flex cr-items-center cr-justify-center cr-rounded-xl cr-bg-gray-50 cr-text-gray-500"
        style={{ height: `${height}px` }}
      >
        {noDataMessage}
      </div>
    );
  }

  return (
    <ChartCard height={`${height}px`} isLoading={isLoading}>
      <div ref={plotRef} className="cr-h-full cr-w-full" />
    </ChartCard>
  );
}
